import { useCallback, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useUserConfig } from '@/features/_shared/user';
import { optionById } from '@/features/data-hub/constants/exportOptions';
import type {
  ExportMetaContextType,
  ExportNameContextType,
  ExportPreviewContextType,
  ExportRequestContextType,
  ExportSettingsContextType,
  ExportSettingsType
} from '@/features/data-hub/types';
import { DateRangeType, defaultExportSettings, ExportTypes } from '@/features/data-hub/types';
import { SELECTOR_MODES } from '@/features/symbol-selector';
import copyToClipboard from '@/shared/lib/copyToClipboard';
import { useAppSelector } from '@/store/hooks';
import { DrawerType } from '@/store/slices/drawer.slice';

import useExportMutation from '../api/useExportMutation';
import useExportAnalytics from './useExportAnalytics';
import useExportApiStrings from './useExportApiStrings';
import useExportName from './useExportName';

export type UseDataHubExportReturn = {
  settings: ExportSettingsContextType;
  preview: ExportPreviewContextType;
  request: ExportRequestContextType;
  meta: ExportMetaContextType;
  name: ExportNameContextType;
};

const useDataHubExport = (): UseDataHubExportReturn => {
  // State
  const { type: exportSliceType, selectedTimeRange } = useAppSelector(state => state.export);
  const isExportDrawerOpen = useAppSelector(state => state.drawer.openDrawer === DrawerType.EXPORT);
  const populateDefaultWithExportSetting = useCallback(() => {
    const periodNumber =
      selectedTimeRange?.replaceAll(/\D/g, '') || defaultExportSettings.periodNumber;
    const period = selectedTimeRange?.replaceAll(/[^a-zA-Z]/g, '') || defaultExportSettings.period;
    const dateRangeType =
      selectedTimeRange === undefined ? defaultExportSettings.dateRangeType : DateRangeType.RANGE;
    const exportType =
      exportSliceType && exportSliceType === ExportTypes.DATA
        ? ExportTypes.DATA
        : ExportTypes.IMAGE;
    return {
      ...defaultExportSettings,
      period,
      periodNumber,
      dateRangeType,
      exportType
    };
  }, [exportSliceType, selectedTimeRange]);
  const [exportSettings, setExportSettings] = useState<ExportSettingsType>(
    populateDefaultWithExportSetting()
  );
  const [previewOptionId, setPreviewOptionId] = useState<string | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewHasNoData, setPreviewHasNoData] = useState<boolean>(false);
  const [previewDownloadFn, setPreviewDownloadFn] = useState<(() => void) | null>(null);
  const [previewContentToCopy, setPreviewContentToCopy] = useState<string | null>(null);
  const wasExportDrawerOpenRef = useRef(isExportDrawerOpen);

  // Selectors
  const symbols = useAppSelector(state => state.symbolSelector.symbols);
  const selectorMode = useAppSelector(state => state.symbolSelector.selectorMode);
  const { data: userConfigData } = useUserConfig();
  const userTimezone = userConfigData?.timezone.value ?? 'UTC';
  const userDateFormat = userConfigData?.dateFormat.value ?? 'dd-MMM-yy';

  // Derived
  const uniqueSymbols = useMemo(() => new Set(symbols), [symbols]);
  const isForwardCurve = selectorMode === SELECTOR_MODES.FORWARD_CURVE;
  const exportOption = previewOptionId ? (optionById.get(previewOptionId) ?? null) : null;

  // Composed hooks
  const { exportName, setExportName, createExportName } = useExportName();
  const { capturePosthogEvent, captureCopyEvent } = useExportAnalytics(
    exportOption,
    exportSettings
  );
  const { exportApiRequestString, exportCurlRequestString } = useExportApiStrings({
    symbols,
    exportSettings,
    userTimezone
  });
  const { exportDataMutation, loading, exportStatus, setExportStatus, resetExportStatus } =
    useExportMutation({
      exportName,
      exportSettings,
      symbols,
      userTimezone,
      userDateFormat,
      isForwardCurve,
      createExportName,
      setExportName,
      setPreviewOptionId
    });

  // Handlers
  const updateExportSetting = useCallback(
    <K extends keyof ExportSettingsType>(key: K, value: ExportSettingsType[K]) => {
      setExportSettings(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleCopyToClipBoard = useCallback(
    (textToCopy: string) => {
      captureCopyEvent();
      copyToClipboard(textToCopy, 'Copied to clipboard!');
    },
    [captureCopyEvent]
  );

  const handleExportData = useCallback(async () => {
    if (loading) return;
    if (previewOptionId) {
      return exportDataMutation({ type: previewOptionId });
    }
  }, [exportDataMutation, previewOptionId, loading]);

  const syncExportState = useCallback(() => {
    setExportSettings(populateDefaultWithExportSetting());
    setPreviewOptionId(null);
    setPreviewHasNoData(false);
    setShowSuccessScreen(false);
    setExportName(createExportName());
  }, [createExportName, setExportName, populateDefaultWithExportSetting]);

  const stableSyncExportState = useEffectEvent(syncExportState);

  useLayoutEffect(() => {
    if (isExportDrawerOpen && !wasExportDrawerOpenRef.current) {
      stableSyncExportState();
    }

    wasExportDrawerOpenRef.current = isExportDrawerOpen;
  }, [isExportDrawerOpen]);

  const resetExportState = useCallback(() => {
    syncExportState();
  }, [syncExportState]);

  // Structured return - each object matches its context type
  const settings: ExportSettingsContextType = useMemo(
    () => ({ exportSettings, setExportSettings, updateExportSetting }),
    [exportSettings, updateExportSetting]
  );

  const preview: ExportPreviewContextType = useMemo(
    () => ({
      previewOptionId,
      setPreviewOptionId,
      previewLoading,
      setPreviewLoading,
      previewHasNoData,
      setPreviewHasNoData,
      previewDownloadFn,
      setPreviewDownloadFn,
      previewContentToCopy,
      setPreviewContentToCopy
    }),
    [previewOptionId, previewLoading, previewHasNoData, previewDownloadFn, previewContentToCopy]
  );

  const request: ExportRequestContextType = useMemo(
    () => ({
      exportDataWithParams: exportDataMutation,
      loading,
      showSuccessScreen,
      setShowSuccessScreen,
      exportStatus,
      setExportStatus,
      resetExportStatus,
      handleCopyToClipBoard,
      handleExportData,
      capturePosthogEvent,
      resetExportState
    }),
    [
      exportDataMutation,
      loading,
      showSuccessScreen,
      exportStatus,
      setExportStatus,
      resetExportStatus,
      handleCopyToClipBoard,
      handleExportData,
      capturePosthogEvent,
      resetExportState
    ]
  );

  const meta: ExportMetaContextType = useMemo(
    () => ({
      uniqueSymbols,
      exportApiRequestString,
      exportCurlRequestString,
      timezone: userTimezone
    }),
    [uniqueSymbols, exportApiRequestString, exportCurlRequestString, userTimezone]
  );

  const name: ExportNameContextType = useMemo(
    () => ({
      exportName,
      setExportName
    }),
    [exportName, setExportName]
  );

  return { settings, preview, request, meta, name };
};

export default useDataHubExport;
